/**
 * Aktive/Inaktive Flotten-Listen Synchronisation für das TravelMenu.
 * Rendert Progress-Bars (aktiv) und Flotten-Karten (inaktiv).
 */
import type { TravelMenuState, ActiveTravel } from './TravelMenuState';
import { createTravelProgressBar, updateTravelProgressBar } from './TravelProgressBar';

/** Synchronisiert die aktiven (reisenden) Flotten-Einträge. */
export function syncActiveTravels(
  container: HTMLDivElement,
  state: TravelMenuState,
  barEls: Map<string, HTMLDivElement>,
): void {
  const travels = state.travelingTravels;

  if (travels.length === 0) {
    container.innerHTML = '';
    barEls.clear();
    const empty = document.createElement('span');
    empty.dataset.placeholder = '1';
    empty.textContent = 'Keine aktiven Flotten';
    Object.assign(empty.style, { color: '#5a7a9a', fontSize: 'clamp(9px, 2.2vw, 11px)' });
    container.appendChild(empty);
    return;
  }

  // Entferne veraltete Einträge
  for (const [id, el] of barEls) {
    if (!_hasId(travels, id)) { el.remove(); barEls.delete(id); }
  }

  const placeholder = container.querySelector('[data-placeholder]');
  if (placeholder) placeholder.remove();

  for (const t of travels) {
    const barData = state.getBarData(t);
    const existing = barEls.get(t.id);
    if (existing) {
      updateTravelProgressBar(existing, barData);
    } else {
      const bar = createTravelProgressBar(barData);
      container.appendChild(bar);
      barEls.set(t.id, bar);
    }
  }
}

/** Synchronisiert die inaktiven (stationierten) Flotten-Einträge. */
export function syncInactiveTravels(
  container: HTMLDivElement,
  state: TravelMenuState,
  inactiveEls: Map<string, HTMLDivElement>,
  onSendFromHex: (fleet: ActiveTravel) => void,
): void {
  const fleets = state.stationedTravels;

  if (fleets.length === 0) {
    container.innerHTML = '';
    inactiveEls.clear();
    const empty = document.createElement('span');
    empty.dataset.placeholder = '1';
    empty.textContent = 'Keine stationierten Flotten';
    Object.assign(empty.style, { color: '#5a7a9a', fontSize: 'clamp(9px, 2.2vw, 11px)' });
    container.appendChild(empty);
    return;
  }

  // Entferne veraltete Einträge
  for (const [id, el] of inactiveEls) {
    if (!_hasId(fleets, id)) { el.remove(); inactiveEls.delete(id); }
  }

  const placeholder = container.querySelector('[data-placeholder]');
  if (placeholder) placeholder.remove();

  for (const f of fleets) {
    if (inactiveEls.has(f.id)) continue;
    const card = _createInactiveCard(f, onSendFromHex);
    container.appendChild(card);
    inactiveEls.set(f.id, card);
  }
}

/** Prüft ob eine ID in der Liste enthalten ist (ohne .map/.filter Allokation). */
function _hasId(list: readonly ActiveTravel[], id: string): boolean {
  for (let i = 0; i < list.length; i++) {
    if (list[i].id === id) return true;
  }
  return false;
}

/** Erzeugt eine Karte für eine stationierte Flotte. */
function _createInactiveCard(
  fleet: ActiveTravel,
  onSend: (fleet: ActiveTravel) => void,
): HTMLDivElement {
  const card = document.createElement('div');
  Object.assign(card.style, {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 10px', background: 'rgba(10,18,30,0.6)',
    borderRadius: '6px', border: '1px solid #1a2a3a', marginBottom: '4px',
  });

  const icon = document.createElement('span');
  icon.textContent = '\u2B21';
  Object.assign(icon.style, { fontSize: '18px', color: '#d4a020', flexShrink: '0' });

  const info = document.createElement('div');
  Object.assign(info.style, { display: 'flex', flexDirection: 'column', gap: '1px', flex: '1' });

  const loc = document.createElement('span');
  loc.textContent = `Hex (${fleet.end.q}, ${fleet.end.r})`;
  Object.assign(loc.style, { color: '#8cf', fontSize: '12px', fontWeight: '700' });

  const ships = document.createElement('span');
  let totalShips = 0;
  for (let i = 0; i < fleet.ships.length; i++) totalShips += fleet.ships[i];
  ships.textContent = `${totalShips} Schiffe stationiert`;
  Object.assign(ships.style, { color: '#5a7a9a', fontSize: '10px' });

  const sendBtn = document.createElement('button');
  sendBtn.textContent = '\u{1F680} Senden';
  Object.assign(sendBtn.style, {
    background: 'rgba(100,180,255,0.1)', color: '#8cf',
    border: '1px solid rgba(100,180,255,0.25)',
    fontFamily: 'monospace', cursor: 'pointer',
    fontSize: '10px', padding: '4px 10px', borderRadius: '6px', flexShrink: '0',
  });
  sendBtn.addEventListener('pointerdown', (e) => {
    e.stopPropagation();
    onSend(fleet);
  });

  info.appendChild(loc);
  info.appendChild(ships);
  card.appendChild(icon);
  card.appendChild(info);
  card.appendChild(sendBtn);
  return card;
}
